import type { Options } from "react-markdown";

type RemarkPlugins = NonNullable<Options["remarkPlugins"]>;
type RemarkPlugin = RemarkPlugins[number];

interface MarkdownNode {
  type: string;
  value?: string;
  url?: string;
  title?: string | null;
  children?: MarkdownNode[];
}

interface TextNode extends MarkdownNode {
  type: "text";
  value: string;
}

const MATCH_PATTERN =
  /(^|[^A-Za-z0-9_@])((https?:\/\/[^\s<]+|www\.[^\s<]+)|([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}))/gi;

const SKIP_PARENT_TYPES = new Set([
  "link",
  "linkReference",
  "image",
  "imageReference",
  "definition",
  "inlineCode",
  "code",
  "html",
]);

const TRAILING_PUNCTUATION = /[!"',.:;<>?\]}]+$/;

const countChar = (value: string, char: string) => {
  let count = 0;

  for (const current of value) {
    if (current === char) {
      count += 1;
    }
  }

  return count;
};

const trimTrailingUrlPunctuation = (value: string) => {
  const trailingMatch = TRAILING_PUNCTUATION.exec(value);

  if (!trailingMatch) {
    return { linkValue: value, trailingText: "" };
  }

  let linkValue = value.slice(0, trailingMatch.index);
  let trailingText = trailingMatch[0];
  let closingParenIndex = trailingText.indexOf(")");
  let openingParens = countChar(linkValue, "(");
  let closingParens = countChar(linkValue, ")");

  while (closingParenIndex !== -1 && openingParens > closingParens) {
    linkValue += trailingText.slice(0, closingParenIndex + 1);
    trailingText = trailingText.slice(closingParenIndex + 1);
    closingParenIndex = trailingText.indexOf(")");
    closingParens += 1;
  }

  return { linkValue, trailingText };
};

const trimTrailingEmailPunctuation = (value: string) => {
  const trimmed = value.replace(/[),.:;!?]+$/, "");
  return {
    linkValue: trimmed,
    trailingText: value.slice(trimmed.length),
  };
};

const createTextNode = (value: string): TextNode => ({
  type: "text",
  value,
});

const createLinkNode = (value: string, url: string): MarkdownNode => ({
  type: "link",
  title: null,
  url,
  children: [createTextNode(value)],
});

const transformText = (value: string) => {
  const nodes: MarkdownNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null = MATCH_PATTERN.exec(value);

  while (match) {
    const prefix = match[1] ?? "";
    const candidate = match[2] ?? "";
    const matchIndex = match.index + prefix.length;

    if (matchIndex > lastIndex) {
      nodes.push(createTextNode(value.slice(lastIndex, matchIndex)));
    } else if (prefix) {
      nodes.push(createTextNode(prefix));
    }

    const isUrl = Boolean(match[3]);
    const { linkValue, trailingText } = isUrl
      ? trimTrailingUrlPunctuation(candidate)
      : trimTrailingEmailPunctuation(candidate);

    if (!linkValue) {
      nodes.push(createTextNode(candidate));
      lastIndex = match.index + match[0].length;
      match = MATCH_PATTERN.exec(value);
      continue;
    }

    nodes.push(
      createLinkNode(
        linkValue,
        isUrl
          ? linkValue.startsWith("www.")
            ? `http://${linkValue}`
            : linkValue
          : `mailto:${linkValue}`,
      ),
    );

    if (trailingText) {
      nodes.push(createTextNode(trailingText));
    }

    lastIndex = match.index + match[0].length;
    match = MATCH_PATTERN.exec(value);
  }

  if (lastIndex === 0) {
    return [createTextNode(value)];
  }

  if (lastIndex < value.length) {
    nodes.push(createTextNode(value.slice(lastIndex)));
  }

  return nodes;
};

const shouldSkipNode = (ancestors: MarkdownNode[]) => {
  return ancestors.some((ancestor) => SKIP_PARENT_TYPES.has(ancestor.type));
};

const visitTextNodes = (node: MarkdownNode, ancestors: MarkdownNode[] = []) => {
  if (!node.children) {
    return;
  }

  for (let index = 0; index < node.children.length; index += 1) {
    const child = node.children[index];

    if (child.type === "text" && child.value && !shouldSkipNode(ancestors)) {
      const replacement = transformText(child.value);
      const changed =
        replacement.length !== 1 || replacement[0].value !== child.value;

      if (changed) {
        node.children.splice(index, 1, ...replacement);
        index += replacement.length - 1;
        continue;
      }
    }

    if (child.children) {
      visitTextNodes(child, [...ancestors, child]);
    }
  }
};

export const remarkAutolinkFallback = (() => {
  return (tree: MarkdownNode) => {
    visitTextNodes(tree);
  };
}) as RemarkPlugin;
